import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { Medal, Mic2, Trophy, Users } from 'lucide-react';
import {
  fetchTournamentDetail,
  registerForTournament,
  withdrawFromTournament,
} from '~/api/tournaments';
import { getSession, useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';
import { formatCountdown, useCountdown } from '~/ui/Countdown';
import { Button } from '~/ui/components/button';

export const Route = createFileRoute('/tournaments/$id')({
  head: ({ params }) =>
    buildHead({
      title: 'Tournament',
      description: 'Tournament bracket, entrants, and results.',
      path: `/tournaments/${params.id}`,
    }),
  beforeLoad: async ({ location }) => {
    const session = await getSession();
    if (!session) {
      throw redirect({
        to: '/auth/sign-in',
        search: { next: location.pathname },
      });
    }
  },
  component: TournamentDetail,
});

function TournamentDetail() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const { data, isLoading, error } = useQuery({
    queryKey: ['tournament', id],
    queryFn: () => fetchTournamentDetail(id),
    refetchInterval: 15_000,
  });
  const registerMut = useMutation({
    mutationFn: () => registerForTournament(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tournament', id] }),
  });
  const withdrawMut = useMutation({
    mutationFn: () => withdrawFromTournament(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tournament', id] }),
  });

  const startsCountdown = useCountdown(data?.startsAt ?? null);
  const regCountdown = useCountdown(
    data && data.status === 'open' ? data.registrationClosesAt : null,
  );
  const showcaseCountdown = useCountdown(
    data && data.status === 'showcase' ? (data.showcaseEndsAt ?? null) : null,
  );

  if (isLoading) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12">
        <p className="text-sm text-muted-foreground">Loading...</p>
      </section>
    );
  }
  if (error || !data) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 space-y-3">
        <h1 className="text-2xl font-bold">Tournament not found</h1>
        <Link to="/tournaments" className="underline text-sm">
          Back to tournaments
        </Link>
      </section>
    );
  }

  const t = data;
  const pending = registerMut.isPending || withdrawMut.isPending;
  const canRegister = session && t.status === 'open' && !t.registered;
  const canWithdraw = session && t.status === 'open' && t.registered;
  const isShowcase = t.status === 'showcase';

  // Group bracket matches by round for display.
  const rounds = new Map<number, typeof t.bracket>();
  for (const m of t.bracket) {
    const list = rounds.get(m.round) ?? [];
    list.push(m);
    rounds.set(m.round, list);
  }
  const roundKeys = [...rounds.keys()].sort((a, b) => a - b);

  return (
    <article className="mx-auto max-w-3xl px-4 py-12 space-y-8">
      <div>
        <Link
          to="/tournaments"
          className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
        >
          ← all tournaments
        </Link>
      </div>

      <header className="space-y-3">
        <div className="flex flex-wrap items-start gap-3">
          <Trophy className="size-6 text-primary mt-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-bold tracking-tight">{t.name}</h1>
            <p className="text-sm text-muted-foreground">
              {t.genreName} · {t.entrantCount}/{t.maxEntrants} entrants ·{' '}
              {new Date(t.startsAt).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
          {t.status === 'open' && regCountdown !== null && (
            <span className="text-amber-300">
              registration closes in{' '}
              <span className="font-mono">{formatCountdown(regCountdown)}</span>
            </span>
          )}
          {isShowcase && showcaseCountdown !== null && (
            <span className="text-violet-300">
              showcase closes in{' '}
              <span className="font-mono">{formatCountdown(showcaseCountdown)}</span>
            </span>
          )}
          {(t.status === 'open' || t.status === 'starting') && startsCountdown !== null && (
            <span className="text-muted-foreground">
              starts in <span className="font-mono">{formatCountdown(startsCountdown)}</span>
            </span>
          )}
          {t.status === 'finished' && t.winnerHandle && (
            <span className="text-emerald-300">
              winner: <span className="font-semibold">@{t.winnerHandle}</span>
            </span>
          )}
        </div>
        {canRegister && (
          <div className="space-y-1">
            <Button onClick={() => registerMut.mutate()} disabled={pending}>
              {pending ? 'Registering...' : 'Register'}
            </Button>
            {registerMut.isError && (
              <p className="text-xs text-destructive">{String(registerMut.error)}</p>
            )}
          </div>
        )}
        {canWithdraw && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-emerald-400">You're registered.</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => withdrawMut.mutate()}
              disabled={pending}
            >
              {pending ? '...' : 'Withdraw'}
            </Button>
          </div>
        )}
        {isShowcase && (
          <div className="rounded-lg border border-violet-500/40 bg-violet-500/10 p-4 flex items-start gap-3">
            <Mic2 className="size-5 text-violet-400 shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <div className="text-sm font-medium text-violet-300">Showcase phase open</div>
              <p className="text-xs text-muted-foreground">
                {t.bracketEnabled
                  ? 'Entrants are uploading their tracks. Listen and vote to influence the crowd favorite, then the bracket kicks off.'
                  : 'One track decides the week. Upload your track and collect votes. The producer with the most votes when this closes wins.'}
              </p>
              <Button asChild size="sm" variant="outline">
                <Link to="/tournaments/$id/showcase" params={{ id: t.id }}>
                  Listen + vote
                  {showcaseCountdown !== null && (
                    <span className="ml-1 font-mono text-xs text-muted-foreground">
                      ({formatCountdown(showcaseCountdown)} left)
                    </span>
                  )}
                </Link>
              </Button>
            </div>
          </div>
        )}
        {!session && t.status === 'open' && (
          <Button asChild variant="outline" size="sm">
            <Link to="/auth/sign-in">Sign in to register</Link>
          </Button>
        )}
      </header>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Users className="size-5" /> Entrants
        </h2>
        {t.entrants.length === 0 ? (
          <p className="text-sm text-muted-foreground">No entrants yet. Be the first.</p>
        ) : (
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {t.entrants.map((e) => (
              <li key={e.handle} className="rounded-md border border-border px-3 py-1.5 text-sm">
                <Link
                  to="/$username"
                  params={{ username: e.handle }}
                  className="hover:text-primary hover:underline"
                >
                  @{e.handle}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Showcase-only finished: show leaderboard instead of bracket */}
      {!t.bracketEnabled && t.status === 'finished' && t.showcaseLeaderboard.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Medal className="size-5 text-amber-400" /> Final standings
          </h2>
          <ul className="space-y-1.5">
            {t.showcaseLeaderboard.map((entry) => (
              <li
                key={entry.handle}
                className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2 text-sm"
              >
                <span className="font-mono text-xs text-muted-foreground w-6 text-right">
                  #{entry.finalRank}
                </span>
                {entry.finalRank === 1 && <Trophy className="size-4 text-amber-400 shrink-0" />}
                <Link
                  to="/$username"
                  params={{ username: entry.handle }}
                  className="hover:text-primary hover:underline"
                >
                  @{entry.handle}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Bracket tournament: show bracket with note on round 1 */}
      {t.bracketEnabled && t.bracket.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-xl font-semibold">Bracket</h2>
          {roundKeys[0] === 1 && (
            <p className="text-xs text-muted-foreground rounded-md border border-border px-3 py-2">
              Round 1 votes are on showcase tracks - winners upload fresh from round 2.
            </p>
          )}
          <div className="space-y-4">
            {roundKeys.map((r) => {
              const matches = rounds.get(r) ?? [];
              return (
                <div key={r} className="space-y-2">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    Round {r}
                  </div>
                  <ul className="space-y-1.5">
                    {matches.map((m) => (
                      <li key={m.matchId}>
                        <Link
                          to="/room/$code"
                          params={{ code: m.roomCode }}
                          className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card px-3 py-2 text-sm hover:border-primary"
                        >
                          <span className="font-mono text-xs text-muted-foreground">
                            {m.roomCode}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {m.players.map((p) => `@${p}`).join(' vs ')}
                          </span>
                          <span className="ml-auto text-xs">
                            {m.winner ? (
                              <span className="text-emerald-400">@{m.winner}</span>
                            ) : (
                              <span className="text-muted-foreground">{m.status}</span>
                            )}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </article>
  );
}
